import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { knoxApi, type AuthResponse, type OrgInfo, KnoxApiError } from './knox-api';

const authChannel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('knox-auth') : null;

/** Consume pending_join_token from localStorage and call join-org API.
 *  Uses a guard to prevent double-consumption from concurrent calls
 *  (e.g. register + session restore racing). */
let joinConsumed = false;
/** Returns true while consumeJoinToken is in-flight (token removed but API not yet resolved). */
export function isJoinInProgress(): boolean { return joinConsumed; }
async function consumeJoinToken(): Promise<AuthResponse | null> {
  const token = localStorage.getItem('pending_join_token');
  if (!token || joinConsumed) return null;
  joinConsumed = true;
  localStorage.removeItem('pending_join_token');
  localStorage.removeItem('pending_invite_token');
  try {
    const resp = await knoxApi.joinOrgLoggedIn({ token });
    return resp;
  } catch {
    joinConsumed = false; // Allow retry on failure
    return null;
  }
}

/** Read ?redirect= from URL, fallback to user's default workspace. Prevent open redirect. */
function getSafeRedirect(defaultWorkspaceId?: string): string {
  const param = new URLSearchParams(window.location.search).get('redirect');
  if (param && param.startsWith('/') && !param.startsWith('//')) {
    return param;
  }
  if (defaultWorkspaceId) {
    return `/workspaces/${defaultWorkspaceId}`;
  }
  return '/workspaces';
}

interface AuthState {
  user: AuthResponse | null;
  loading: boolean;
  error: string | null;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string, opts?: { skipRedirect?: boolean; recaptchaToken?: string; recaptchaType?: 'checkbox' }) => Promise<void>;
  register: (email: string, password: string, name: string, opts?: { recaptchaToken?: string; recaptchaType?: 'checkbox'; emailVerifiedToken?: string }) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  /** Set password for an OAuth-registered user and issue session. */
  setPassword: (email: string, password: string, opts?: { recaptchaToken?: string; recaptchaType?: 'checkbox'; emailVerifiedToken?: string }) => Promise<AuthResponse | null>;
  /** Refresh session from server and update state. Returns the user data. */
  refreshSession: () => Promise<AuthResponse | null>;
  /** Patch user fields in-place (e.g. after profile update). */
  patchUser: (fields: Partial<Pick<AuthResponse, 'name' | 'email' | 'avatar'>>) => void;
  /** All organizations the user belongs to. */
  orgs: OrgInfo[];
  /** The currently active organization (matched by profile org_id). */
  currentOrg: OrgInfo | null;
  /** Switch to a different organization. Reloads the page after switching. */
  switchOrg: (orgId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
  });
  const [orgs, setOrgs] = useState<OrgInfo[]>([]);
  const [currentOrg, setCurrentOrg] = useState<OrgInfo | null>(null);
  const navigate = useNavigate();

  /** Load org list + current org from server. */
  const loadOrgs = useCallback(async () => {
    const orgsData = await knoxApi.getMyOrgs().catch(() => ({ orgs: [] as OrgInfo[] }));
    const orgList = orgsData.orgs ?? [];
    setOrgs(orgList);
    if (orgList.length > 0) {
      const sessionOrgId = state.user?.org_id;
      if (sessionOrgId) {
        setCurrentOrg(orgList.find(o => o.id === sessionOrgId) ?? orgList[0]);
      } else {
        const profile = await knoxApi.getProfile().catch(() => null);
        setCurrentOrg(orgList.find(o => o.id === profile?.org_id) ?? orgList[0]);
      }
    }
  }, [state.user?.org_id]);

  // Restore session on mount (run once)
  const sessionRestored = useRef(false);
  useEffect(() => {
    if (sessionRestored.current) return;
    sessionRestored.current = true;
    knoxApi.getSession({ silent: true })
      .then(async (user) => {
        if (user.language && user.language !== i18next.language) {
          i18next.changeLanguage(user.language);
        }
        setState({ user, loading: false, error: null });
        if (user.org_id) {
          await loadOrgs();
        }
      })
      .catch(() => setState({ user: null, loading: false, error: null }));
  }, [loadOrgs]);

  // Redirect to login when another tab logs out.
  useEffect(() => {
    if (!authChannel) return;
    const handler = (e: MessageEvent) => {
      if (e.data?.type === 'logout') {
        setState({ user: null, loading: false, error: null });
        navigate('/login', { replace: true });
      }
    };
    authChannel.addEventListener('message', handler);
    return () => authChannel.removeEventListener('message', handler);
  }, [navigate]);

  const { t } = useTranslation('knox-aiops');
  const login = useCallback(async (email: string, password: string, opts?: { skipRedirect?: boolean; recaptchaToken?: string; recaptchaType?: 'checkbox' }) => {
    setState(s => ({ ...s, error: null }));
    try {
      const user = await knoxApi.login({ email, password }, { recaptchaToken: opts?.recaptchaToken, recaptchaType: opts?.recaptchaType });
      // Sync language preference from server after login
      if (user.language && user.language !== i18next.language) {
        i18next.changeLanguage(user.language);
      }
      setState({ user, loading: false, error: null });
      // Only load orgs when user has an org — avoids 401 "no API key" toast
      if (user.org_id) {
        // Clear any pending tokens — user already has an org, no need to consume them
        localStorage.removeItem('pending_join_token');
        localStorage.removeItem('pending_invite_token');
        await loadOrgs();
        if (!opts?.skipRedirect) {
          const target = getSafeRedirect(user.default_workspace_id);
          navigate(target, { replace: true });
        }
      } else {
        // No org — auto-join if pending join token exists
        const joinResult = await consumeJoinToken();
        if (joinResult) {
          // Use replace() to drop the join_token URL — reload would re-trigger
          // LoginPage Effect 2 which re-populates localStorage, causing a
          // second join-org call that fails (400 already consumed).
          window.location.replace('/workspaces');
          return;
        }
        // No join token — go to org-binding
        if (!opts?.skipRedirect) {
          navigate('/org-binding', { replace: true });
        }
      }
    } catch (err) {
      // NEED_CHALLENGE must propagate to the caller (EmailLoginForm) to show checkbox
      if (err instanceof KnoxApiError && err.code === 'NEED_CHALLENGE') {
        setState(s => ({ ...s, loading: false }));
        throw err;
      }
      const message = err instanceof Error ? err.message : t('error.loginFailed');
      setState(s => ({ ...s, loading: false, error: message }));
    }
  }, [navigate, t, loadOrgs]);

  const register = useCallback(async (email: string, password: string, name: string, opts?: { recaptchaToken?: string; recaptchaType?: 'checkbox'; emailVerifiedToken?: string }) => {
    setState(s => ({ ...s, error: null }));
    try {
      const user = await knoxApi.register({ email, password, name }, { recaptchaToken: opts?.recaptchaToken, recaptchaType: opts?.recaptchaType, emailVerifiedToken: opts?.emailVerifiedToken });
      setState({ user, loading: false, error: null });
      // No org — auto-join if pending join token exists
      if (!user.org_id) {
        const joinResult = await consumeJoinToken();
        if (joinResult) {
          // Use replace() to drop the join_token URL — reload would re-trigger
          // LoginPage Effect 2 which re-populates localStorage, causing a
          // second join-org call that fails (400 already consumed).
          window.location.replace('/workspaces');
          return;
        }
      }
      // No join token or join failed — navigate to org-binding
      navigate('/org-binding', { replace: true });
    } catch (err) {
      if (err instanceof KnoxApiError && err.code === 'NEED_CHALLENGE') throw err;
      const message = err instanceof Error ? err.message : t('error.registrationFailed');
      setState(s => ({ ...s, loading: false, error: message }));
    }
  }, [t, navigate]);

  const logout = useCallback(async () => {
    await knoxApi.logout();
    setState({ user: null, loading: false, error: null });
    authChannel?.postMessage({ type: 'logout' });
    navigate('/login', { replace: true });
  }, [navigate]);

  const clearError = useCallback(() => {
    setState(s => (s.error ? { ...s, error: null } : s));
  }, []);

  const setPassword = useCallback(async (email: string, password: string, opts?: { recaptchaToken?: string; recaptchaType?: 'checkbox'; emailVerifiedToken?: string }): Promise<AuthResponse | null> => {
    setState(s => ({ ...s, error: null }));
    try {
      const user = await knoxApi.setPassword(email, password, { recaptchaToken: opts?.recaptchaToken, recaptchaType: opts?.recaptchaType, emailVerifiedToken: opts?.emailVerifiedToken });
      if (user.language && user.language !== i18next.language) {
        i18next.changeLanguage(user.language);
      }
      setState({ user, loading: false, error: null });
      await loadOrgs();
      return user;
    } catch (err) {
      if (err instanceof KnoxApiError && err.code === 'NEED_CHALLENGE') {
        throw err;
      }
      const message = err instanceof Error ? err.message : t('error.loginFailed');
      setState(s => ({ ...s, loading: false, error: message }));
      return null;
    }
  }, [t, loadOrgs]);

  const patchUser = useCallback((fields: Partial<Pick<AuthResponse, 'name' | 'email' | 'avatar'>>) => {
    setState(s => (s.user ? { ...s, user: { ...s.user, ...fields } } : s));
  }, []);

  const refreshSession = useCallback(async (): Promise<AuthResponse | null> => {
    try {
      const user = await knoxApi.getSession();
      if (user.language && user.language !== i18next.language) {
        i18next.changeLanguage(user.language);
      }
      setState({ user, loading: false, error: null });
      await loadOrgs();
      return user;
    } catch {
      return null;
    }
  }, [loadOrgs]);

  const switchOrg = useCallback(async (orgId: string) => {
    await knoxApi.switchOrg(orgId);
    // Full reload: workspace, rooms, Matrix credentials all change with org
    window.location.reload();
  }, []);

  return (
    <AuthContext.Provider value={{
      ...state, login, register, logout, clearError, patchUser, refreshSession, setPassword,
      orgs, currentOrg, switchOrg,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
